import React, { useState } from 'react'
import { X, Download, FileText, Database, Calendar, User } from 'lucide-react'
import toast from 'react-hot-toast'

const ExportModal = ({ isOpen, onClose, dataInput, onExport }) => {
  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    includeHeaders: true,
    includeMetadata: false,
    includeDeleted: false,
    selectedColumns: dataInput.attributes?.filter(attr => attr.is_active).map(attr => attr.name) || [],
    filename: '',
    dateRangeEnabled: false,
    dateFrom: '',
    dateTo: ''
  })

  const [isExporting, setIsExporting] = useState(false)

  if (!isOpen) return null

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await onExport(exportOptions)
      onClose()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  const handleColumnToggle = (columnName) => {
    setExportOptions(prev => ({
      ...prev,
      selectedColumns: prev.selectedColumns.includes(columnName)
        ? prev.selectedColumns.filter(col => col !== columnName)
        : [...prev.selectedColumns, columnName]
    }))
  }

  const selectAllColumns = () => {
    const allColumns = activeAttributes.map(attr => attr.name)
    setExportOptions(prev => ({
      ...prev,
      selectedColumns: allColumns
    }))
  }

  const deselectAllColumns = () => {
    setExportOptions(prev => ({
      ...prev,
      selectedColumns: []
    }))
  }

  const activeAttributes = dataInput.attributes?.filter(attr => attr.is_active) || []

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Download className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
              <p className="text-sm text-gray-600">{dataInput.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Export Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'csv', label: 'CSV', icon: FileText, description: 'Comma-separated values' },
                { value: 'xlsx', label: 'Excel', icon: Database, description: 'Excel spreadsheet' },
                { value: 'json', label: 'JSON', icon: FileText, description: 'JavaScript Object Notation' }
              ].map((format) => (
                <button
                  key={format.value}
                  onClick={() => setExportOptions(prev => ({ ...prev, format: format.value }))}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    exportOptions.format === format.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    <format.icon className="h-4 w-4 mr-2" />
                    <span className="font-medium">{format.label}</span>
                  </div>
                  <p className="text-xs text-gray-600">{format.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Export Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Export Options
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={exportOptions.includeHeaders}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Include column headers</span>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={exportOptions.includeMetadata}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-2">
                  <span className="text-sm text-gray-700">Include metadata fields</span>
                  <div className="text-xs text-gray-500 mt-1 flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>Created/Updated dates</span>
                    <User className="h-3 w-3 ml-3 mr-1" />
                    <span>Created/Updated by</span>
                  </div>
                </div>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={exportOptions.includeDeleted}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, includeDeleted: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Include deleted records</span>
              </label>
            </div>
          </div>

          {/* Column Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Columns to Export
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={selectAllColumns}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllColumns}
                  className="text-xs text-gray-600 hover:text-gray-800"
                >
                  Deselect All
                </button>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
              {activeAttributes.length === 0 ? (
                <p className="text-sm text-gray-500">No active attributes available</p>
              ) : (
                <div className="space-y-2">
                  {activeAttributes.map((attr) => (
                    <label key={attr.name} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exportOptions.selectedColumns.includes(attr.name)}
                        onChange={() => handleColumnToggle(attr.name)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{attr.label || attr.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({attr.data_type})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {exportOptions.selectedColumns.length === 0
                ? 'All columns will be exported'
                : `${exportOptions.selectedColumns.length} column(s) selected`}
            </p>
          </div>

          {/* Date Range Filter */}
          <div>
            <div className="flex items-center mb-3">
              <Calendar className="h-5 w-5 text-gray-500 mr-2" />
              <label className="block text-sm font-medium text-gray-700">
                Filter by Record Creation Date
              </label>
            </div>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={exportOptions.dateRangeEnabled}
                  onChange={(e) => setExportOptions(prev => ({
                    ...prev,
                    dateRangeEnabled: e.target.checked,
                    dateFrom: e.target.checked ? prev.dateFrom : '',
                    dateTo: e.target.checked ? prev.dateTo : ''
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-700">Enable date range filtering</span>
              </label>

              {exportOptions.dateRangeEnabled && (
                <div className="grid grid-cols-2 gap-3 ml-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={exportOptions.dateFrom}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      To Date
                    </label>
                    <input
                      type="date"
                      value={exportOptions.dateTo}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {exportOptions.dateRangeEnabled && (
              <p className="text-xs text-gray-500 mt-2">
                Only records created within the specified date range will be exported
              </p>
            )}
          </div>

          {/* Custom Filename */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Filename (optional)
            </label>
            <input
              type="text"
              value={exportOptions.filename}
              onChange={(e) => setExportOptions(prev => ({ ...prev, filename: e.target.value }))}
              placeholder={`${dataInput.name}_export`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || activeAttributes.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export Data
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExportModal
